/**
 * formationConsistencyEngine.js
 *
 * Master coordinator for the Spiritual Formation + Pastoral Identity layer.
 * Combines all 7 formation modules into ONE compact system prompt block.
 *
 * Answers: "What kind of pastoral presence should Kevia maintain right now
 * to be spiritually consistent, mature, and genuinely helpful?"
 *
 * Output: ONE [FORMACIÓN PASTORAL] block appended to the system prompt.
 * Omitted when all signals are baseline (no unnecessary token spend).
 *
 * Design:
 *  — Pure function; no async, no side effects, no localStorage
 *  — Consumes assessment.emotion (no duplicate detection)
 *  — Consumes discernment.wisdomTone + discernment.timing (no duplication with FASE 6)
 *  — All 7 modules run; only non-null lines are injected
 */

import { resolveSpiritualPresence }    from "./spiritualPresenceEngine";
import { resolveIdentitySignal }       from "./pastoralIdentityResolver";
import { detectTheologicalCentering }  from "./theologicalCenteringEngine";
import { balanceReverenceAndTone }     from "./reverenceAndToneBalancer";
import { assessHopeStability }         from "./hopeStabilityEngine";
import { buildSpiritualLanguageGuidance } from "./spiritualLanguageFilter";
import { resolveMinistryPosture }      from "./ministryPostureEngine";

const MAX_FORMATION_CHARS = 420;

/**
 * @param {{
 *   text:               string,
 *   intent:             string,
 *   emotion:            object|null,   — from assessment.emotion (no re-detection)
 *   mood:               string,
 *   riskLevel:          string,
 *   wisdomTone:         string,        — from discernment.wisdomTone
 *   timing:             string,        — from discernment.timing
 *   conversationLength: number,
 * }} params
 * @returns {{
 *   formationBlock:    string|null,
 *   presence:          string,
 *   posture:           string,
 *   hopeLevel:         "stable"|"calibrating",
 *   reverenceLevel:    "low"|"medium"|"high",
 *   hasLanguageFilter: boolean,
 * }}
 */
export function buildFormationContext({
  text,
  intent,
  emotion,
  mood,
  riskLevel          = "none",
  wisdomTone         = "pastoral",
  timing             = "respond_now",
  conversationLength = 0,
}) {
  // ── Run all 7 modules ────────────────────────────────────────────
  const presenceResult  = resolveSpiritualPresence({ mood, emotion, intent, wisdomTone, riskLevel });
  const identityResult  = resolveIdentitySignal({ intent, mood, conversationLength });
  const centeringResult = detectTheologicalCentering({ emotion, intent, mood, riskLevel });
  const reverenceResult = balanceReverenceAndTone({ intent, emotion, mood, wisdomTone, conversationLength });
  const hopeResult      = assessHopeStability({ text, emotion, mood, riskLevel });
  const languageResult  = buildSpiritualLanguageGuidance({ text, intent, emotion });
  const postureResult   = resolveMinistryPosture({ emotion, mood, intent, timing, riskLevel });

  // ── Strict gate — R7: raised to require 2+ simultaneous formation signals ──
  // Single-signal formation is usually covered by base personality or other layers.
  // Reverence "high" always fires alone — it's rare and safety-relevant.
  const formationConditions = [
    presenceResult.instruction !== null,
    postureResult.instruction  !== null,
    hopeResult.needsCalibration,
  ].filter(Boolean).length;

  const hasDistinctShift =
    formationConditions >= 2 ||
    reverenceResult.reverenceLevel === "high";

  if (!hasDistinctShift) {
    return {
      formationBlock:    null,
      presence:          presenceResult.presence,
      posture:           postureResult.posture,
      hopeLevel:         hopeResult.needsCalibration ? "calibrating" : "stable",
      reverenceLevel:    reverenceResult.reverenceLevel,
      hasLanguageFilter: languageResult.hasFilter,
    };
  }

  // ── Collect instruction lines (priority order) ────────────────────
  const lines = [];
  if (presenceResult.instruction)                                           lines.push(presenceResult.instruction);
  if (postureResult.instruction)                                            lines.push(postureResult.instruction);
  if (centeringResult.instruction)                                          lines.push(centeringResult.instruction);
  if (hopeResult.instruction)                                               lines.push(hopeResult.instruction);
  if (reverenceResult.reverenceLevel === "high" && reverenceResult.instruction) lines.push(reverenceResult.instruction);

  const formationBlock = lines.length > 0
    ? lines.join("\n").slice(0, MAX_FORMATION_CHARS)
    : null;

  return {
    formationBlock,
    presence:          presenceResult.presence,
    posture:           postureResult.posture,
    hopeLevel:         hopeResult.needsCalibration ? "calibrating" : "stable",
    reverenceLevel:    reverenceResult.reverenceLevel,
    hasLanguageFilter: languageResult.hasFilter,
  };
}
